import { Plus } from '@phosphor-icons/react';
import { cn } from '@reelplicate/ui';
import {
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from 'react';

import nowDot from './assets/now-dot.svg';
import pastHatch from './assets/past-hatch.png';
import { type PostStatus, STATUS_LABELS } from './calendar-model';
import { CHIP_MINUTES, layoutLanes } from './lanes';
import type { PlatformMark } from './platform-stack';
import { PostChip } from './post-chip';
import { type CalendarDrag, dropTime } from './use-drag-move';
import {
  FIRST_HOUR,
  formatHour,
  formatTime,
  HOUR_COUNT,
  HOUR_HEIGHT,
  isSameDay,
  minutesFromTop,
  SCROLL_TO_HOUR,
  VISIBLE_HOURS,
} from './week';

/** A post as the grid draws it: already filtered to visible accounts. */
export type GridPost = {
  id: string;
  title: string;
  start: Date;
  /** Short account name for the meta line ("O&E"). */
  account: string;
  status: PostStatus;
  thumbnail: string | null;
  /** Where it goes, with where it stands on each. */
  platforms: PlatformMark[];
  /** Still to go out, so it can be dragged to another time. */
  movable: boolean;
};

const GRID_MINUTES = HOUR_COUNT * 60;
const HOURS = Array.from({ length: HOUR_COUNT }, (_, i) => FIRST_HOUR + i);

/** Where a chip sits: its time, kept inside the grid (a post at 23:55 sits at its bottom edge). */
const chipTop = (start: Date) =>
  Math.min(Math.max(minutesFromTop(start), 0), GRID_MINUTES - CHIP_MINUTES) + 3;

/** Figma day view: chips keep their week-ish width and sit side by side. */
const DAY_CHIP_WIDTH = 340;
const DAY_CHIP_GAP = 8;
/** The day headers (h-14). */
const HEADER_HEIGHT = 56;

type WeekGridProps = {
  /** The columns: a week's 7 days, or a single day. */
  days: Date[];
  now: Date;
  posts: GridPost[];
  onOpenPost: (post: GridPost) => void;
  onSchedule: (at: Date) => void;
  /** Drag a post to another time. */
  drag: CalendarDrag;
};

/**
 * Figma "Week grid" (143:1232) and "Calendar · Day" (369:5217): day columns
 * by 24 hour rows (60px each), scrolling inside the card: it opens on 8 AM,
 * or on the current hour when today is shown and it's before 8 or after 7 PM,
 * with the day headers kept in view. Time that has passed is hatched,
 * today's header is Pale Sage with a red now line, and empty future slots
 * offer "Schedule post" on hover. With one day, the header adds a summary and
 * posts at the same time sit side by side. Scheduled posts drag to another
 * time, landing on the quarter hour.
 */
export function WeekGrid({
  days,
  now,
  posts,
  onOpenPost,
  onSchedule,
  drag,
}: WeekGridProps) {
  const single = days.length === 1;
  const scroller = useRef<HTMLDivElement>(null);
  const showsToday = days.some((day) => isSameDay(day, now));
  const firstDay = days[0]?.toDateString();
  // Open on the working day, or on now when it's outside it - once per range
  // shown, so a re-render (the clock ticking) doesn't undo someone's scroll.
  useLayoutEffect(() => {
    const hour = now.getHours();
    const top =
      showsToday && (hour < SCROLL_TO_HOUR || hour >= SCROLL_TO_HOUR + VISIBLE_HOURS - 1)
        ? Math.max(hour - 1, 0)
        : SCROLL_TO_HOUR;
    if (scroller.current) scroller.current.scrollTop = top * HOUR_HEIGHT;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the range changes
  }, [firstDay, days.length]);
  return (
    <div className="bg-bg-surface border-border-subtle w-full overflow-hidden rounded-2xl border">
      <div
        ref={scroller}
        data-testid="week-scroller"
        className="overflow-auto"
        style={{ maxHeight: HEADER_HEIGHT + VISIBLE_HOURS * HOUR_HEIGHT }}
      >
        <div
          className="grid min-w-[840px]"
          style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}
        >
          <div className="border-border-subtle bg-bg-surface sticky top-0 z-30 h-14 border-b" />
          {days.map((day) =>
            single ? (
              <DaySummaryHeader
                key={day.toISOString()}
                day={day}
                today={isSameDay(day, now)}
                posts={posts}
              />
            ) : (
              <DayHeader key={day.toISOString()} day={day} today={isSameDay(day, now)} />
            ),
          )}
          <TimeGutter />
          {days.map((day, i) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              now={now}
              showPassedLabel={i === 0}
              posts={posts.filter((p) => isSameDay(p.start, day))}
              fixedWidthLanes={single}
              onOpenPost={onOpenPost}
              onSchedule={onSchedule}
              drag={drag}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayHeader({ day, today }: { day: Date; today: boolean }) {
  return (
    <div
      className={cn(
        'border-border-subtle sticky top-0 z-30 flex h-14 flex-col items-center justify-center border-b border-l',
        today ? 'bg-brand-subtle text-text-brand' : 'bg-bg-surface text-text-primary',
      )}
    >
      <span
        className={cn(
          'text-overline uppercase',
          today ? 'text-text-brand' : 'text-text-tertiary',
        )}
      >
        {day.toLocaleString('en-US', { weekday: 'short' })}
      </span>
      <span className={today ? 'text-heading-h4' : 'text-body-lg-strong'}>
        {day.getDate()}
        {today && <span className="sr-only"> (today)</span>}
      </span>
    </div>
  );
}

/** Day view header: "THU 24" on the left, the day's post counts on the right. */
function DaySummaryHeader({
  day,
  today,
  posts,
}: {
  day: Date;
  today: boolean;
  posts: GridPost[];
}) {
  const counts = (
    ['failed', 'attention', 'published', 'scheduled', 'review', 'draft'] as const
  )
    .map((status) => [status, posts.filter((p) => p.status === status).length] as const)
    .filter(([, n]) => n > 0)
    .map(([status, n]) => `${n} ${STATUS_LABELS[status].toLowerCase()}`);
  return (
    <div
      className={cn(
        'border-border-subtle sticky top-0 z-30 flex h-14 items-center justify-between gap-4 border-b border-l px-4',
        today ? 'bg-brand-subtle' : 'bg-bg-surface',
      )}
    >
      <p className="flex items-center gap-2">
        <span
          className={cn(
            'text-overline uppercase',
            today ? 'text-text-brand' : 'text-text-tertiary',
          )}
        >
          {day.toLocaleString('en-US', { weekday: 'short' })}
        </span>
        <span
          className={cn(
            'text-heading-h4',
            today ? 'text-text-brand' : 'text-text-primary',
          )}
        >
          {day.getDate()}
          {today && <span className="sr-only"> (today)</span>}
        </span>
      </p>
      {posts.length > 0 && (
        <p className="text-caption text-text-secondary truncate">
          {posts.length} {posts.length === 1 ? 'post' : 'posts'} — {counts.join(' · ')}
        </p>
      )}
    </div>
  );
}

/** Time that has passed: a subtle fill with the diagonal hatch on top. */
export function PastHatch({
  className,
  style,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div aria-hidden className={cn('bg-bg-subtle absolute', className)} style={style}>
      <div
        className="absolute inset-0 opacity-[0.22]"
        style={{ backgroundImage: `url("${pastHatch}")`, backgroundSize: '10px 10px' }}
      />
      {children}
    </div>
  );
}

function TimeGutter() {
  return (
    <div aria-hidden>
      {HOURS.map((hour) => (
        <div key={hour} style={{ height: HOUR_HEIGHT }} className="pt-1 text-center">
          <span className="text-caption text-text-tertiary">{formatHour(hour)}</span>
        </div>
      ))}
    </div>
  );
}

function DayColumn({
  day,
  now,
  showPassedLabel,
  posts,
  fixedWidthLanes,
  onOpenPost,
  onSchedule,
  drag,
}: {
  day: Date;
  now: Date;
  showPassedLabel: boolean;
  posts: GridPost[];
  /** Day view: Figma-width chips side by side, not shared column width. */
  fixedWidthLanes: boolean;
  onOpenPost: (post: GridPost) => void;
  onSchedule: (at: Date) => void;
  drag: CalendarDrag;
}) {
  const today = isSameDay(day, now);
  const past = !today && day < now;
  const nowMinutes = minutesFromTop(now);
  const pastMinutes = past
    ? GRID_MINUTES
    : today
      ? Math.min(Math.max(nowMinutes, 0), GRID_MINUTES)
      : 0;
  const dayLabel = day.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const lanes = layoutLanes(posts);
  const chipBox = (id: string): CSSProperties => {
    const { lane, lanes: count } = lanes.get(id) ?? { lane: 0, lanes: 1 };
    if (fixedWidthLanes) {
      return { left: 5 + lane * (DAY_CHIP_WIDTH + DAY_CHIP_GAP), width: DAY_CHIP_WIDTH };
    }
    return {
      left: `calc(5px + (100% - 10px) * ${lane / count})`,
      width: `calc((100% - 10px) / ${count} - ${count > 1 ? 2 : 0}px)`,
    };
  };

  const taken = (hour: number) => {
    const from = (hour - FIRST_HOUR) * 60;
    return posts.some((p) => {
      const start = minutesFromTop(p.start);
      return start < from + 60 && start + CHIP_MINUTES > from;
    });
  };

  // Where a drag over this column would land.
  const landing = (event: DragEvent) =>
    drag.dragging &&
    dropTime(
      day,
      event.clientY - event.currentTarget.getBoundingClientRect().top,
      drag.dragging.grabOffset,
    );
  const target = drag.target && isSameDay(drag.target, day) ? drag.target : null;

  return (
    <div
      data-testid={`day-${day.getDate()}`}
      className="border-border-subtle relative border-l"
      style={{ height: HOUR_COUNT * HOUR_HEIGHT }}
      onDragOver={(event) => drag.over(event, landing(event))}
      onDrop={(event) => drag.drop(event, landing(event))}
    >
      {pastMinutes > 0 && (
        <PastHatch className="inset-x-0 top-0" style={{ height: pastMinutes }}>
          {past && showPassedLabel && (
            // Where the grid opens, so it's in view.
            <p
              className="text-caption text-text-tertiary absolute inset-x-0 text-center"
              style={{ top: SCROLL_TO_HOUR * HOUR_HEIGHT + 20 }}
            >
              Date passed
            </p>
          )}
        </PastHatch>
      )}

      {HOURS.map((hour, i) => {
        const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour);
        return (
          <div
            key={hour}
            style={{ height: HOUR_HEIGHT }}
            className={cn(
              'relative',
              i < HOUR_COUNT - 1 && 'border-border-subtle border-b',
            )}
          >
            {at >= now && !taken(hour) && !drag.dragging && (
              // Pointer shortcut only: "Create post" is the keyboard path, so
              // 91 slots don't all join the tab order.
              <button
                type="button"
                tabIndex={-1}
                aria-label={`Schedule post, ${dayLabel}, ${formatHour(hour)}`}
                onClick={() => onSchedule(at)}
                className="bg-brand-subtle border-border-brand text-label-sm text-text-brand absolute inset-x-[5px] top-[3px] bottom-[3px] flex items-center justify-center gap-1.5 rounded-md border border-dashed opacity-0 transition-opacity duration-[120ms] ease-out hover:opacity-100"
              >
                <Plus aria-hidden className="size-3.5" />
                Schedule post
              </button>
            )}
          </div>
        );
      })}

      {today && nowMinutes >= 0 && nowMinutes <= GRID_MINUTES && (
        <div
          aria-hidden
          data-testid="now-line"
          className="bg-status-danger-solid pointer-events-none absolute inset-x-0 z-20 h-0.5"
          style={{ top: nowMinutes }}
        >
          <img src={nowDot} alt="" className="absolute top-[-4px] left-[-5px]" />
        </div>
      )}

      {target && (
        <div
          aria-hidden
          data-testid="drop-indicator"
          className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
          style={{ top: minutesFromTop(target) - 1 }}
        >
          <span className="bg-brand-primary text-caption text-text-inverse ml-1 rounded-xs px-1.5 py-0.5 tabular-nums">
            {formatTime(target)}
          </span>
          <span className="bg-brand-primary h-0.5 flex-1" />
        </div>
      )}

      <ul aria-label={dayLabel} className="pointer-events-none absolute inset-0 z-10">
        {posts.map((post) => (
          <li
            key={post.id}
            draggable={post.movable}
            onDragStart={(event) => drag.start(event, post)}
            onDragEnd={drag.end}
            className={cn(
              'pointer-events-auto absolute',
              post.movable && 'cursor-grab active:cursor-grabbing',
              drag.dragging?.id === post.id && 'opacity-50',
            )}
            style={{ top: chipTop(post.start), ...chipBox(post.id) }}
          >
            <PostChip
              title={post.title}
              meta={`${formatTime(post.start)} · ${post.account}`}
              status={post.status}
              thumbnail={post.thumbnail}
              platforms={post.platforms}
              className="w-full"
              onClick={() => onOpenPost(post)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
